import { useEffect, useMemo, useState } from "react";
import { History, Search } from "lucide-react";
import { api, apiErrorDetail } from "@/lib/api";

const STATUS_META = {
  pending: { label: "In attesa", cls: "bg-peach text-deeptaupe" },
  confirmed: { label: "Confermato", cls: "bg-deeptaupe text-ivory" },
  rejected: { label: "Rifiutato", cls: "bg-red-100 text-red-700" },
  cancelled: { label: "Annullato", cls: "bg-charcoal/10 text-charcoal/60" },
  expired: { label: "Scaduto", cls: "bg-taupe/30 text-charcoal/70" },
};

export default function HistoryTab({ flash }) {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);

  const load = () => {
    api
      .get("/admin/appointments")
      .then(({ data }) => setItems(data.appointments || []))
      .catch((e) => setError(apiErrorDetail(e)));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = search.trim().toLowerCase();
    return items
      .filter((a) =>
        !q ||
        [a.name, a.phone, a.email, a.service_name, a.date].some((v) => (v || "").toLowerCase().includes(q))
      )
      .sort((a, b) => (b.date + b.start).localeCompare(a.date + a.start));
  }, [items, search]);

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-2xl lg:text-3xl flex items-center gap-3">
          <History size={24} className="text-deeptaupe" /> Storico appuntamenti
        </h2>
        <p className="text-sm text-charcoal/60 mt-2 max-w-2xl">
          Cronologia completa di tutti gli appuntamenti passati per il sito, dal più recente: rifiutati,
          annullati, scaduti, confermati e in attesa. I record dello storico non sono eliminabili.
        </p>
      </div>

      <label className="block relative mb-6">
        <Search size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-charcoal/40" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per nome, telefono, email, servizio o data…"
          data-testid="history-search"
          className="w-full rounded-full border border-taupe/40 bg-white px-12 py-3 text-sm focus:border-deeptaupe focus:outline-none"
        />
      </label>

      {error && (
        <p className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5" data-testid="history-error">
          {error}
        </p>
      )}

      {items === null && !error ? (
        <p className="py-16 text-center text-charcoal/50 text-sm" data-testid="history-loading">Caricamento dello storico…</p>
      ) : filtered.length === 0 ? (
        <p className="py-16 text-center text-charcoal/50 text-sm bg-white rounded-3xl" data-testid="history-empty">
          {items && items.length === 0 ? "Non ci sono ancora appuntamenti nello storico." : "Nessun appuntamento trovato con questa ricerca."}
        </p>
      ) : (
        <div className="space-y-2" data-testid="history-list">
          {filtered.map((a) => {
            const meta = STATUS_META[a.status] || STATUS_META.pending;
            return (
              <div key={a.mid} className="flex flex-wrap items-center gap-3 bg-white rounded-2xl px-5 py-4" data-testid={`history-row-${a.mid}`}>
                <span className="text-sm font-medium w-full sm:w-40 shrink-0 capitalize" data-testid={`history-date-${a.mid}`}>
                  {new Intl.DateTimeFormat("it-IT", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(a.date + "T12:00:00"))}
                </span>
                <span className="font-display text-xl text-deeptaupe w-24 sm:w-28 shrink-0">{a.start}–{a.end}</span>
                <span className="flex-1 min-w-[180px]">
                  <span className="block text-sm font-medium">{a.name}</span>
                  <span className="block text-[13px] text-charcoal/60">{a.service_name} · {a.phone || "—"}{a.email ? ` · ${a.email}` : ""}</span>
                </span>
                <span className={`text-[11px] uppercase tracking-widest2 font-medium rounded-full px-3 py-1 ${meta.cls}`} data-testid={`history-status-${a.mid}`}>
                  {meta.label}
                </span>
                <span className="text-[10px] uppercase tracking-widest2 text-charcoal/40">{a.source === "admin" ? "Manuale" : "Sito"}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
